import axios from 'axios'
import { API_CONFIG } from '../config/api'
import type {
  WorkspaceDeal, WorkspaceDealDetail, WorkspaceSolutionTrack,
  WorkspaceStakeholder, WorkspaceActivity, WorkspaceRequirement,
  WorkspaceNote, WorkspaceThreat, WorkspaceShortlistEntry,
  WorkspaceQuote, WorkspaceRegistration, WorkspaceConfiguration,
  WorkspaceActionItem, CreateDealInput, CreateTrackInput,
  CreateStakeholderInput, CreateActivityInput, CreateRequirementInput,
  CreateNoteInput, CreateThreatInput, CreateActionItemInput,
  DealOutcome, SolutionTrackStage, ConfigurationStatus,
  V3ActionStatus,
  WorkspaceQuestion, WorkspaceAnswer, FilteredSupplier,
  WorkspaceExtraction, WorkspaceSignal, WorkspacePrepBrief,
  AnswerScope, AnswerSourceType,
  AccountSolution, UpdateSolutionPayload,
  AnswerResult, QuestionsResponse
} from '../types/workspace'

const api = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout
})

const headers = (token: string) => ({ Authorization: `Bearer ${token}` })

// DEALS
export async function getWorkspaceDeals(token: string): Promise<WorkspaceDeal[]> {
  const res = await api.get('/api/advisors/workspace/deals', { headers: headers(token) })
  return res.data
}

export async function getWorkspaceDeal(dealId: string, token: string): Promise<WorkspaceDealDetail> {
  const res = await api.get(`/api/advisors/workspace/deals/${dealId}`, { headers: headers(token) })
  return res.data
}

export async function createWorkspaceDeal(data: CreateDealInput, token: string): Promise<WorkspaceDeal> {
  const res = await api.post('/api/advisors/workspace/deals', data, { headers: headers(token) })
  return res.data
}

export async function updateWorkspaceDeal(dealId: string, data: Partial<WorkspaceDeal>, token: string): Promise<WorkspaceDeal> {
  const res = await api.put(`/api/advisors/workspace/deals/${dealId}`, data, { headers: headers(token) })
  return res.data
}

export async function setDealOutcome(dealId: string, outcome: DealOutcome, reason?: string, token?: string): Promise<WorkspaceDeal> {
  const res = await api.put(`/api/advisors/workspace/deals/${dealId}/outcome`, { outcome, outcome_reason: reason }, { headers: headers(token!) })
  return res.data
}

// SOLUTION TRACKS
export async function createSolutionTrack(dealId: string, data: CreateTrackInput, token: string): Promise<WorkspaceSolutionTrack> {
  const res = await api.post(`/api/advisors/workspace/deals/${dealId}/tracks`, data, { headers: headers(token) })
  return res.data
}

export async function updateSolutionTrack(trackId: string, data: Partial<WorkspaceSolutionTrack>, token: string): Promise<WorkspaceSolutionTrack> {
  const res = await api.put(`/api/advisors/workspace/tracks/${trackId}`, data, { headers: headers(token) })
  return res.data
}

export async function advanceSolutionTrackStage(trackId: string, stage: SolutionTrackStage, token: string): Promise<WorkspaceSolutionTrack> {
  const res = await api.put(`/api/advisors/workspace/tracks/${trackId}/stage`, { stage }, { headers: headers(token) })
  return res.data
}

// STAKEHOLDERS
export async function getStakeholders(accountId: string, token: string): Promise<WorkspaceStakeholder[]> {
  const res = await api.get(`/api/advisors/workspace/accounts/${accountId}/stakeholders`, { headers: headers(token) })
  return res.data
}

export async function createStakeholder(accountId: string, data: CreateStakeholderInput, token: string): Promise<WorkspaceStakeholder> {
  const res = await api.post(`/api/advisors/workspace/accounts/${accountId}/stakeholders`, data, { headers: headers(token) })
  return res.data
}

export async function updateStakeholder(id: string, data: Partial<CreateStakeholderInput>, token: string): Promise<WorkspaceStakeholder> {
  const res = await api.put(`/api/advisors/workspace/stakeholders/${id}`, data, { headers: headers(token) })
  return res.data
}

export async function deleteStakeholder(id: string, token: string): Promise<void> {
  await api.delete(`/api/advisors/workspace/stakeholders/${id}`, { headers: headers(token) })
}

export async function assignDealRole(stakeholderId: string, dealId: string, role: string, token: string) {
  const res = await api.post(`/api/advisors/workspace/stakeholders/${stakeholderId}/deal-roles`, { deal_id: dealId, role }, { headers: headers(token) })
  return res.data
}

export async function removeDealRole(roleId: string, token: string): Promise<void> {
  await api.delete(`/api/advisors/workspace/stakeholder-roles/${roleId}`, { headers: headers(token) })
}

// ACTIVITIES
export async function getActivities(accountId: string, token: string): Promise<WorkspaceActivity[]> {
  const res = await api.get(`/api/advisors/workspace/accounts/${accountId}/activities`, { headers: headers(token) })
  return res.data
}

export async function createActivity(accountId: string, data: CreateActivityInput, token: string): Promise<WorkspaceActivity> {
  const res = await api.post(`/api/advisors/workspace/accounts/${accountId}/activities`, data, { headers: headers(token) })
  return res.data
}

export async function updateActivity(id: string, data: Partial<CreateActivityInput>, token: string): Promise<WorkspaceActivity> {
  const res = await api.put(`/api/advisors/workspace/activities/${id}`, data, { headers: headers(token) })
  return res.data
}

export async function tagActivityToDeal(activityId: string, dealId: string, token: string) {
  const res = await api.post(`/api/advisors/workspace/activities/${activityId}/deal-tags`, { deal_id: dealId }, { headers: headers(token) })
  return res.data
}

export async function untagActivityFromDeal(tagId: string, token: string): Promise<void> {
  await api.delete(`/api/advisors/workspace/activity-deal-tags/${tagId}`, { headers: headers(token) })
}

// REQUIREMENTS
export async function getRequirements(dealId: string, token: string): Promise<WorkspaceRequirement[]> {
  const res = await api.get(`/api/advisors/workspace/deals/${dealId}/requirements`, { headers: headers(token) })
  return res.data
}

export async function createRequirement(dealId: string, data: CreateRequirementInput, token: string): Promise<WorkspaceRequirement> {
  const res = await api.post(`/api/advisors/workspace/deals/${dealId}/requirements`, data, { headers: headers(token) })
  return res.data
}

export async function updateRequirement(id: string, data: Partial<CreateRequirementInput>, token: string): Promise<WorkspaceRequirement> {
  const res = await api.put(`/api/advisors/workspace/requirements/${id}`, data, { headers: headers(token) })
  return res.data
}

export async function deleteRequirement(id: string, token: string): Promise<void> {
  await api.delete(`/api/advisors/workspace/requirements/${id}`, { headers: headers(token) })
}

export async function reorderRequirements(dealId: string, orderedIds: string[], token: string) {
  const res = await api.patch(`/api/advisors/workspace/deals/${dealId}/requirements/reorder`, { ordered_ids: orderedIds }, { headers: headers(token) })
  return res.data
}

// NOTES
export async function getNotes(dealId: string, token: string): Promise<WorkspaceNote[]> {
  const res = await api.get(`/api/advisors/workspace/deals/${dealId}/notes`, { headers: headers(token) })
  return res.data
}

export async function createNote(dealId: string, data: CreateNoteInput, token: string): Promise<WorkspaceNote> {
  const res = await api.post(`/api/advisors/workspace/deals/${dealId}/notes`, data, { headers: headers(token) })
  return res.data
}

export async function updateNote(id: string, data: { text?: string; pinned?: boolean }, token: string): Promise<WorkspaceNote> {
  const res = await api.put(`/api/advisors/workspace/notes/${id}`, data, { headers: headers(token) })
  return res.data
}

export async function deleteNote(id: string, token: string): Promise<void> {
  await api.delete(`/api/advisors/workspace/notes/${id}`, { headers: headers(token) })
}

// COMPETITIVE THREATS
export async function getThreats(dealId: string, token: string): Promise<WorkspaceThreat[]> {
  const res = await api.get(`/api/advisors/workspace/deals/${dealId}/competitive`, { headers: headers(token) })
  return res.data
}

export async function createThreat(dealId: string, data: CreateThreatInput, token: string): Promise<WorkspaceThreat> {
  const res = await api.post(`/api/advisors/workspace/deals/${dealId}/competitive`, data, { headers: headers(token) })
  return res.data
}

export async function updateThreat(id: string, data: Partial<CreateThreatInput>, token: string): Promise<WorkspaceThreat> {
  const res = await api.put(`/api/advisors/workspace/competitive/${id}`, data, { headers: headers(token) })
  return res.data
}

export async function deleteThreat(id: string, token: string): Promise<void> {
  await api.delete(`/api/advisors/workspace/competitive/${id}`, { headers: headers(token) })
}

// SUPPLIER SHORTLIST
export async function getShortlistSuppliers(trackId: string, token: string): Promise<WorkspaceShortlistEntry[]> {
  const res = await api.get(`/api/advisors/workspace/tracks/${trackId}/suppliers`, { headers: headers(token) })
  return res.data
}

export async function addShortlistSupplier(trackId: string, data: { supplier_id: string; products?: string[] }, token: string): Promise<WorkspaceShortlistEntry> {
  const res = await api.post(`/api/advisors/workspace/tracks/${trackId}/suppliers`, data, { headers: headers(token) })
  return res.data
}

// Double opt-in: after shortlisting, the advisor can ask the supplier to reach
// out — notifies the supplier's team with the advisor's context.
export async function requestShortlistIntro(shortlistId: string, token: string): Promise<{ success?: boolean; already_requested?: boolean }> {
  const res = await api.post(`/api/advisors/workspace/shortlist/${shortlistId}/request-intro`, {}, { headers: headers(token) })
  return res.data
}

// v4 model: shortlist a supplier on an account solution (not a v3 track)
export async function addShortlistSupplierV4(accountId: string, solutionId: string, data: { supplier_id: string; products?: string[] }, token: string): Promise<unknown> {
  const res = await api.post(`/api/advisors/workspace/accounts/${accountId}/solutions/${solutionId}/shortlist`, data, { headers: headers(token) })
  return res.data
}

export async function updateShortlistEntry(id: string, data: Record<string, unknown>, token: string): Promise<WorkspaceShortlistEntry> {
  const res = await api.put(`/api/advisors/workspace/shortlist/${id}`, data, { headers: headers(token) })
  return res.data
}

export async function removeShortlistEntry(id: string, token: string): Promise<void> {
  await api.delete(`/api/advisors/workspace/shortlist/${id}`, { headers: headers(token) })
}

// QUOTES
export async function getTrackQuotes(trackId: string, token: string): Promise<WorkspaceQuote[]> {
  const res = await api.get(`/api/advisors/workspace/tracks/${trackId}/quotes`, { headers: headers(token) })
  return res.data
}

export async function createTrackQuote(trackId: string, data: Record<string, unknown>, token: string): Promise<WorkspaceQuote> {
  const res = await api.post(`/api/advisors/workspace/tracks/${trackId}/quotes`, data, { headers: headers(token) })
  return res.data
}

export async function updateTrackQuote(id: string, data: Record<string, unknown>, token: string): Promise<WorkspaceQuote> {
  const res = await api.put(`/api/advisors/workspace/quotes/${id}`, data, { headers: headers(token) })
  return res.data
}

// REGISTRATIONS
export async function getRegistrations(trackId: string, token: string): Promise<WorkspaceRegistration[]> {
  const res = await api.get(`/api/advisors/workspace/tracks/${trackId}/registrations`, { headers: headers(token) })
  return res.data
}

export async function createRegistration(trackId: string, data: Record<string, unknown>, token: string): Promise<WorkspaceRegistration> {
  const res = await api.post(`/api/advisors/workspace/tracks/${trackId}/registrations`, data, { headers: headers(token) })
  return res.data
}

export async function updateRegistration(id: string, data: Record<string, unknown>, token: string): Promise<WorkspaceRegistration> {
  const res = await api.put(`/api/advisors/workspace/registrations/${id}`, data, { headers: headers(token) })
  return res.data
}

// CONFIGURATIONS
export async function getConfigurations(dealId: string, token: string): Promise<WorkspaceConfiguration[]> {
  const res = await api.get(`/api/advisors/workspace/deals/${dealId}/configurations`, { headers: headers(token) })
  return res.data
}

export async function createConfiguration(dealId: string, data: Record<string, unknown>, token: string): Promise<WorkspaceConfiguration> {
  const res = await api.post(`/api/advisors/workspace/deals/${dealId}/configurations`, data, { headers: headers(token) })
  return res.data
}

export async function updateConfiguration(id: string, data: Record<string, unknown>, token: string): Promise<WorkspaceConfiguration> {
  const res = await api.put(`/api/advisors/workspace/configurations/${id}`, data, { headers: headers(token) })
  return res.data
}

export async function setConfigStatus(id: string, status: ConfigurationStatus, token: string): Promise<WorkspaceConfiguration> {
  const res = await api.put(`/api/advisors/workspace/configurations/${id}/status`, { status }, { headers: headers(token) })
  return res.data
}

// ACTION ITEMS
export async function getActionItems(dealId: string, token: string, status?: V3ActionStatus): Promise<WorkspaceActionItem[]> {
  const params = status ? { status } : {}
  const res = await api.get(`/api/advisors/workspace/deals/${dealId}/actions`, { headers: headers(token), params })
  return res.data
}

export async function createActionItem(dealId: string, data: CreateActionItemInput, token: string): Promise<WorkspaceActionItem> {
  const res = await api.post(`/api/advisors/workspace/deals/${dealId}/actions`, data, { headers: headers(token) })
  return res.data
}

export async function updateActionItem(id: string, data: Record<string, unknown>, token: string): Promise<WorkspaceActionItem> {
  const res = await api.put(`/api/advisors/workspace/actions/${id}`, data, { headers: headers(token) })
  return res.data
}

// ============================================
// Phase 2: Questions & Answers
// ============================================

export async function getQuestions(token: string, solutionType?: string): Promise<WorkspaceQuestion[]> {
  const params = solutionType ? { solutionType } : {}
  const res = await api.get('/api/advisors/workspace/questions', { headers: headers(token), params })
  return res.data
}

export interface SolutionRequirement {
  id: string
  subcategory_id: string
  name: string
  label: string
  field_type: 'TEXT' | 'NUMBER' | 'SELECT' | 'MULTI_SELECT' | 'DATE' | 'BOOLEAN'
  options: string[]
  description: string | null
  is_required: boolean
  display_order: number
  category?: QuestionCategory
}

export type QuestionCategory = 'QUALIFICATION' | 'REQUIREMENTS' | 'SUPPLIER_PREFERENCES' | 'EXISTING_SOLUTIONS'

export async function getSolutionRequirements(token: string, subcategoryId: string): Promise<SolutionRequirement[]> {
  const res = await api.get('/api/advisors/workspace/requirements', { headers: headers(token), params: { subcategory_id: subcategoryId } })
  return res.data
}

export async function getAnswersForDeal(dealId: string, token: string): Promise<WorkspaceAnswer[]> {
  const res = await api.get(`/api/advisors/workspace/deals/${dealId}/answers`, { headers: headers(token) })
  return res.data
}

export async function getAnswersForAccount(accountId: string, token: string): Promise<WorkspaceAnswer[]> {
  const res = await api.get(`/api/advisors/workspace/accounts/${accountId}/answers`, { headers: headers(token) })
  return res.data
}

export async function createOrUpdateAnswer(data: {
  question_id?: string
  requirement_id?: string
  scope: AnswerScope
  account_id: string
  deal_id?: string
  solution_track_id?: string
  value: string
  source_type?: AnswerSourceType
}, token: string): Promise<WorkspaceAnswer> {
  const res = await api.post('/api/advisors/workspace/answers', data, { headers: headers(token) })
  return res.data
}

export async function confirmAnswer(answerId: string, token: string) {
  const res = await api.put(`/api/advisors/workspace/answers/${answerId}/confirm`, {}, { headers: headers(token) })
  return res.data
}

export async function dismissAnswer(answerId: string, token: string) {
  const res = await api.put(`/api/advisors/workspace/answers/${answerId}/dismiss`, {}, { headers: headers(token) })
  return res.data
}

export async function getFilteredSuppliersForTrack(trackId: string, token: string): Promise<FilteredSupplier[]> {
  const res = await api.get(`/api/advisors/workspace/tracks/${trackId}/filtered-suppliers`, { headers: headers(token) })
  return res.data
}

// ============================================
// Phase 3: Signals & Extractions
// ============================================

export async function getSignals(dealId: string, token: string, includeDismissed = false): Promise<WorkspaceSignal[]> {
  const params = includeDismissed ? { includeDismissed: 'true' } : {}
  const res = await api.get(`/api/advisors/workspace/deals/${dealId}/signals`, { headers: headers(token), params })
  return res.data
}

export async function generateSignals(dealId: string, token: string): Promise<WorkspaceSignal[]> {
  const res = await api.post(`/api/advisors/workspace/deals/${dealId}/signals/generate`, {}, { headers: headers(token) })
  return res.data
}

export async function dismissSignal(signalId: string, token: string) {
  const res = await api.put(`/api/advisors/workspace/signals/${signalId}/dismiss`, {}, { headers: headers(token) })
  return res.data
}

export async function getExtractions(dealId: string, token: string): Promise<WorkspaceExtraction[]> {
  const res = await api.get(`/api/advisors/workspace/deals/${dealId}/extractions`, { headers: headers(token) })
  return res.data
}

export async function confirmExtraction(extractionId: string, data: Record<string, unknown>, token: string) {
  const res = await api.put(`/api/advisors/workspace/extractions/${extractionId}/confirm`, data, { headers: headers(token) })
  return res.data
}

export async function dismissExtraction(extractionId: string, token: string) {
  const res = await api.put(`/api/advisors/workspace/extractions/${extractionId}/dismiss`, {}, { headers: headers(token) })
  return res.data
}

export async function triggerExtraction(activityId: string, token: string) {
  const res = await api.post(`/api/advisors/workspace/activities/${activityId}/extract`, {}, { headers: headers(token) })
  return res.data
}

// ============================================
// Phase 3: Deal Prep Brief
// ============================================

export async function generatePrepBrief(dealId: string, token: string): Promise<WorkspacePrepBrief> {
  const res = await api.post(`/api/advisors/workspace/deals/${dealId}/prep-brief`, {}, { headers: headers(token) })
  return res.data
}

export async function getPrepBriefs(dealId: string, token: string): Promise<WorkspacePrepBrief[]> {
  const res = await api.get(`/api/advisors/workspace/deals/${dealId}/prep-briefs`, { headers: headers(token) })
  return res.data
}

// ============================================
// Pre-Meeting Prep
// ============================================

export async function generateMeetingPrep(accountId: string, token: string) {
  const res = await api.post(`/api/advisors/workspace/accounts/${accountId}/meeting-prep`, {}, { headers: headers(token) })
  return res.data
}

// ============================================
// Phase 4: Health Recalculation
// ============================================

export async function recalculateDealHealth(dealId: string, token: string) {
  const res = await api.post(`/api/advisors/workspace/deals/${dealId}/health/recalculate`, {}, { headers: headers(token) })
  return res.data
}

// ============================================
// Phase 4: AI Companion Chat
// ============================================

export async function sendAiChat(dealId: string, message: string, token: string): Promise<{ response: string }> {
  const res = await api.post(`/api/advisors/workspace/deals/${dealId}/ai-chat`, { message }, { headers: headers(token) })
  return res.data
}

// ============================================
// RAG Supplier Search
// ============================================

export async function searchSuppliersRAG(query: string, token: string, subcategoryId?: string) {
  const params: Record<string, string> = { query }
  if (subcategoryId) params.subcategory_id = subcategoryId
  const res = await api.get('/api/advisors/workspace/rag/search-suppliers', { headers: headers(token), params })
  return res.data
}

// ============================================
// Supplier Deep Analysis
// ============================================

export async function getSupplierAnalysisData(trackId: string, supplierId: string, token: string) {
  const res = await api.get(`/api/advisors/workspace/tracks/${trackId}/suppliers/${supplierId}/analysis`, { headers: headers(token) })
  return res.data
}

// ============================================
// Solutions Landscape (v4)
// ============================================

export async function uploadAccountDocument(accountId: string, file: File, token: string): Promise<{ filename: string; extractedLength: number; preview: string }> {
  const formData = new FormData()
  formData.append('file', file)
  const res = await api.post(`/api/advisors/workspace/accounts/${accountId}/upload-document`, formData, {
    headers: { ...headers(token), 'Content-Type': 'multipart/form-data' },
    timeout: 30000
  })
  return res.data
}

export async function getAccountLandscape(accountId: string, token: string): Promise<AccountSolution[]> {
  const res = await api.get(`/api/advisors/workspace/accounts/${accountId}/landscape`, { headers: headers(token) })
  return res.data
}

// Re-run AI deal-value estimates for all solutions using current firmographics.
// Async on the backend — watch estimates refresh by polling the landscape.
export async function recomputeEstimates(accountId: string, token: string): Promise<{ started: boolean }> {
  const res = await api.post(`/api/advisors/workspace/accounts/${accountId}/estimates/recompute`, {}, { headers: headers(token) })
  return res.data
}

export async function generateLandscape(accountId: string, token: string): Promise<AccountSolution[]> {
  const res = await api.post(`/api/advisors/workspace/accounts/${accountId}/landscape/generate`, {}, { headers: headers(token) })
  return res.data
}

export interface SolutionCatalogSubcategory { id: string; name: string; slug: string }
export interface SolutionCatalogArea { id: string; name: string; slug: string; subcategories: SolutionCatalogSubcategory[] }

export async function getSolutionCatalog(token: string): Promise<SolutionCatalogArea[]> {
  const res = await api.get(`/api/advisors/workspace/solution-catalog`, { headers: headers(token) })
  return res.data.areas ?? []
}

export async function recalculateLandscape(accountId: string, token: string): Promise<AccountSolution[]> {
  const res = await api.post(`/api/advisors/workspace/accounts/${accountId}/landscape/recalculate`, {}, { headers: headers(token) })
  return res.data
}

export async function updateAccountSolution(accountId: string, solutionId: string, payload: UpdateSolutionPayload, token: string): Promise<AccountSolution> {
  const res = await api.put(`/api/advisors/workspace/accounts/${accountId}/solutions/${solutionId}`, payload, { headers: headers(token) })
  return res.data
}

export async function addAccountSolution(accountId: string, subcategoryId: string, label: string, token: string): Promise<AccountSolution> {
  const res = await api.post(`/api/advisors/workspace/accounts/${accountId}/solutions`, { subcategory_id: subcategoryId, label }, { headers: headers(token) })
  return res.data
}

export async function removeAccountSolution(accountId: string, solutionId: string, token: string): Promise<void> {
  await api.delete(`/api/advisors/workspace/accounts/${accountId}/solutions/${solutionId}`, { headers: headers(token) })
}

export async function updateSolutionEstimate(accountId: string, solutionId: string, estimates: {
  est_mrc_low?: number | null
  est_mrc_high?: number | null
  est_nrc_low?: number | null
  est_nrc_high?: number | null
  est_users?: number | null
  proposed_value?: number | null
}, token: string): Promise<AccountSolution> {
  const res = await api.put(`/api/advisors/workspace/accounts/${accountId}/solutions/${solutionId}/estimate`, estimates, { headers: headers(token) })
  return res.data
}

export interface AccountValue {
  asvCeiling: number
  revenueFloor: number | null
  proposedTotal: number
  rsvTotal: number
  asv: number
  capped: boolean
  proposedVsCeilingPct: number | null
  solutionCount: number
  perSolution: { id: string; proposedValue: number; rsv: number }[]
}

export async function getAccountValue(accountId: string, token: string): Promise<AccountValue> {
  const res = await api.get(`/api/advisors/workspace/accounts/${accountId}/value`, { headers: headers(token) })
  return res.data
}

export interface CrossSolutionSupplier {
  supplier_id: string
  supplier_name: string
  logo_url: string | null
  website_url: string | null
  solution_count: number
  solutions: { name: string; subcategory_id: string }[]
  icp_match: boolean
  shortlisted?: boolean
  in_advisor_network?: boolean
  tsd_network?: { tsd_id: string; tsd_name: string; in_advisor_network: boolean }[]
}

export async function getCrossSolutionSuppliers(accountId: string, token: string, inNetwork?: boolean): Promise<CrossSolutionSupplier[]> {
  const params: Record<string, string> = {}
  if (inNetwork) params.inNetwork = 'true'
  const res = await api.get(`/api/advisors/workspace/accounts/${accountId}/cross-solution-suppliers`, { headers: headers(token), params })
  return res.data
}

export interface DeepAnalysisResult {
  ai: {
    fit_score: number
    fit_summary: string
    strengths: { title: string; detail: string; evidence: string }[]
    weaknesses: { title: string; detail: string; impact: string }[]
    requirement_coverage: { covered: number; total: number; details: { requirement: string; status: string; note: string }[] }
    competitive_notes: string
    talking_points: string[]
    objection_handling: { objection: string; response: string }[]
    risk_factors: string[]
  } | null
  supplier: {
    id: string
    name: string
    slug: string
    logo_url: string | null
    website_url: string | null
    description: string | null
    firmographics: {
      employee_count: number | null
      founded_year: number | null
      headquarters: string | null
      ownership_type: string | null
      employee_growth_yoy_pct: number | null
      annual_revenue_range: string | null
    }
    scores: {
      channel_investment_score: number | null
      product_depth_score: number | null
      financial_stability_score: number | null
    }
    compliance_certifications: string[]
    primary_segments: string[]
    geographic_coverage: string[]
  }
  tsd_affiliations: { tsd_id: string; tsd_name: string; in_advisor_network: boolean }[]
  promotions: { id: string; name: string; headline: string | null; description: string | null; valid_from: string | null; valid_until: string | null; type: string | null; value_type: string | null; value_amount: string | null }[]
  products: { id: string; name: string; description: string | null; subcategories: string[]; certifications: string[]; integrations: string[]; deployment_model: string | null }[]
  solutions_covered: string[]
}

export async function getDeepAnalysis(accountId: string, supplierId: string, token: string): Promise<DeepAnalysisResult> {
  const res = await api.post(`/api/advisors/workspace/accounts/${accountId}/suppliers/${supplierId}/deep-analysis`, {}, { headers: headers(token), timeout: 35000 })
  return res.data
}

export async function enrichAccount(accountId: string, token: string): Promise<any> {
  // Enrichment scrapes the site, runs Gemini, and crawls per-branch pages for
  // addresses — well beyond the default 30s axios timeout. Give it 120s.
  const res = await api.post(`/api/advisors/workspace/accounts/${accountId}/enrich`, {}, { headers: headers(token), timeout: 120000 })
  return res.data
}

export interface SolutionSupplier {
  supplier_id: string
  supplier_name: string
  supplier_logo: string | null
  product_count: number
  products: { id: string; name: string }[]
  tier1_status: string
  tier1_flags: string[]
  tier2_status: string
  tier2_flags: string[]
  certifications: string[]
  employee_count: number | null
  annual_revenue_range: string | null
  founded_year: number | null
  headquarters: string | null
  ownership_type: string | null
  employee_growth_yoy_pct: number | null
  primary_segments: string[]
  channel_investment_score: number | null
  product_depth_score: number | null
  financial_stability_score: number | null
  active_promos: number
  tsd_network: { tsd_id: string; tsd_name: string; in_advisor_network: boolean }[]
  in_advisor_network: boolean
  icp_match?: boolean
}

export async function getSolutionSuppliers(accountId: string, solutionId: string, token: string): Promise<SolutionSupplier[]> {
  const res = await api.get(`/api/advisors/workspace/accounts/${accountId}/solutions/${solutionId}/suppliers`, { headers: headers(token) })
  return res.data
}

// ============================================
// Question Prioritization
// ============================================

export async function getNextQuestions(accountId: string, token: string, options: { limit?: number; phase?: string; ai?: boolean; context?: string; solutionIds?: string[] } = {}): Promise<QuestionsResponse> {
  const { limit = 10, phase = 'auto', ai = false, context, solutionIds } = options
  const params: Record<string, any> = { limit, phase }
  if (ai) params.ai = 'true'
  if (context) params.context = context
  if (solutionIds?.length) params.solution_ids = solutionIds.join(',')
  const res = await api.get(`/api/advisors/workspace/accounts/${accountId}/questions/next`, { headers: headers(token), params })
  return res.data
}

export async function answerQuestion(accountId: string, questionId: string, value: string, token: string, mappedRequirementIds?: string[]): Promise<AnswerResult> {
  const body: any = { value }
  if (mappedRequirementIds?.length) body.mapped_requirement_ids = mappedRequirementIds
  const res = await api.post(`/api/advisors/workspace/accounts/${accountId}/questions/${questionId}/answer`, body, { headers: headers(token) })
  return res.data
}

export async function getQuestionCoverage(accountId: string, token: string) {
  const res = await api.get(`/api/advisors/workspace/accounts/${accountId}/questions/coverage`, { headers: headers(token) })
  return res.data
}

export async function removeMeetingSolution(accountId: string, solutionId: string, reason: string, token: string) {
  const res = await api.post(`/api/advisors/workspace/accounts/${accountId}/meeting/solutions/${solutionId}/remove`, { reason }, { headers: headers(token) })
  return res.data
}

export async function addMeetingSolution(accountId: string, subcategoryId: string, token: string) {
  const res = await api.post(`/api/advisors/workspace/accounts/${accountId}/meeting/solutions/add`, { subcategory_id: subcategoryId }, { headers: headers(token) })
  return res.data
}

// ============================================
// Supplier Ratings
// ============================================

export interface SupplierRatingInput {
  quote_response_time?: number
  implementation_quality?: number
  support_quality?: number
  accuracy_vs_marketing?: number
  overall: number
  deal_size_range?: string
  solution_category?: string
  review_text?: string
}

export async function submitSupplierRating(supplierId: string, data: SupplierRatingInput, token: string): Promise<any> {
  const res = await api.post(`/api/advisors/workspace/suppliers/${supplierId}/ratings`, data, { headers: headers(token) })
  return res.data
}

// ============================================
// Meeting Persistence
// ============================================

export async function createMeeting(accountId: string, data: { meeting_type: string; purpose?: string; attendees?: string; client_priorities?: string; solution_ids?: string[] }, token: string) {
  const res = await api.post(`/api/advisors/workspace/accounts/${accountId}/meetings`, data, { headers: headers(token) })
  return res.data
}

export async function updateMeeting(meetingId: string, data: { notes?: string; status?: string }, token: string) {
  const res = await api.put(`/api/advisors/workspace/meetings/${meetingId}`, data, { headers: headers(token) })
  return res.data
}

export async function logMeetingQuestion(meetingId: string, data: { question_text: string; answer_text?: string; question_phase?: string; solution_label?: string; skipped?: boolean }, token: string) {
  const res = await api.post(`/api/advisors/workspace/meetings/${meetingId}/questions`, data, { headers: headers(token) })
  return res.data
}

export async function getAccountMeetings(accountId: string, token: string) {
  const res = await api.get(`/api/advisors/workspace/accounts/${accountId}/meetings`, { headers: headers(token) })
  return res.data
}

// DEAL INTELLIGENCE
export interface ExpiringContract {
  id: string
  account_id: string
  subcategory_label: string
  status: string
  contract_expiry: string
  current_vendor: string | null
  monthly_cost: number | null
  account: { id: string; name: string; industry: string | null }
}

export interface UpsellOpportunity {
  account_id: string
  account_name: string
  has_solution: string
  suggested_solution: string
  reason: string
}

export async function getExpiringContracts(token: string, days = 90): Promise<ExpiringContract[]> {
  const res = await api.get(`/api/advisors/workspace/intelligence/expiring-contracts?days=${days}`, { headers: headers(token) })
  return res.data
}

export async function getUpsellOpportunities(token: string): Promise<UpsellOpportunity[]> {
  const res = await api.get('/api/advisors/workspace/intelligence/upsell-opportunities', { headers: headers(token) })
  return res.data
}

// ============================================
// RFQ Builder
// ============================================

export interface RfqRequirement {
  name: string
  label: string
  value: string | null
  answered: boolean
}

export interface RfqAdditionalSolution {
  name: string
  solution_id: string
  requirements: RfqRequirement[]
  answered_count: number
  total_count: number
}

export interface RfqShortlistedSupplier {
  supplier_id: string
  supplier_name: string
  supplier_logo: string | null
  products: { id: string; name: string }[]
  quote_status: string
}

export interface RfqPreview {
  account: {
    name: string
    industry: string | null
    size: string | null
    revenue: string | null
    hq_location: string | null
    domain?: string
    company_description?: string
  }
  locations: { name: string | null; city: string; state: string | null; employee_count: number | null; is_headquarters: boolean }[]
  solution_category: string
  requirements: RfqRequirement[]
  answered_count: number
  total_count: number
  additional_solutions: RfqAdditionalSolution[]
  timeline: { contract_expiry: string | null }
  compliance: string[]
  shortlisted_suppliers: RfqShortlistedSupplier[]
}

export async function previewRfq(accountId: string, solutionId: string, options: { anonymous?: boolean; include_solutions?: string[] }, token: string): Promise<RfqPreview> {
  const res = await api.post(`/api/advisors/workspace/accounts/${accountId}/solutions/${solutionId}/rfq/preview`, {
    anonymous: options.anonymous || false,
    include_solutions: options.include_solutions || []
  }, { headers: headers(token) })
  return res.data
}

export async function sendRfq(accountId: string, solutionId: string, data: { supplier_ids: string[]; advisor_notes?: string; anonymous?: boolean; additional_solution_ids?: string[] }, token: string): Promise<unknown[]> {
  const res = await api.post(`/api/advisors/workspace/accounts/${accountId}/solutions/${solutionId}/rfq/send`, data, { headers: headers(token) })
  return res.data
}

export async function recordQuotePricing(accountId: string, quoteId: string, data: { total_mrr?: number; total_nrc?: number; contract_term_months?: number; line_items?: { description: string; quantity: number; unit_mrr?: number; unit_nrc?: number }[]; supplier_notes?: string }, token: string): Promise<unknown> {
  const res = await api.put(`/api/advisors/workspace/accounts/${accountId}/quotes/${quoteId}/receive`, data, { headers: headers(token) })
  return res.data
}

// ============================================
// Stored Account Questions
// ============================================

import type { StoredQuestionsResponse, AnswerStoredQuestionResponse } from '../types/workspace'

export async function getStoredQuestions(accountId: string, token: string): Promise<StoredQuestionsResponse> {
  const res = await api.get(`/api/advisors/workspace/accounts/${accountId}/stored-questions`, { headers: headers(token) })
  return res.data
}

export async function answerStoredQuestion(accountId: string, questionId: string, answerText: string, source: string, token: string): Promise<AnswerStoredQuestionResponse> {
  const res = await api.put(`/api/advisors/workspace/accounts/${accountId}/stored-questions/${questionId}/answer`, { answer_text: answerText, source }, { headers: headers(token) })
  return res.data
}

export async function skipStoredQuestion(accountId: string, questionId: string, token: string): Promise<unknown> {
  const res = await api.put(`/api/advisors/workspace/accounts/${accountId}/stored-questions/${questionId}/skip`, {}, { headers: headers(token) })
  return res.data
}

export async function regenerateStoredQuestions(accountId: string, token: string): Promise<{ status: string }> {
  const res = await api.post(`/api/advisors/workspace/accounts/${accountId}/stored-questions/regenerate`, {}, { headers: headers(token) })
  return res.data
}

// ── Account estate: technologies (existing products) + invoices ──
export interface AccountTechnology {
  id: string
  account_id: string
  vendor_name: string
  product_name: string | null
  category: string | null
  monthly_cost: string | number | null
  annual_cost: string | number | null
  quantity: number | null
  contract_start: string | null
  contract_end: string | null
  satisfaction: string | null
  replacement_priority: string | null
  notes: string | null
  source: string
  source_file_id: string | null
  confidence: number | null
}

export interface AccountInvoice {
  id: string
  account_id: string
  vendor_name: string | null
  invoice_date: string | null
  total_amount: string | number | null
  currency: string | null
  file_name: string | null
  status: 'PROCESSING' | 'EXTRACTED' | 'FAILED'
  error: string | null
  item_count: number
  storage_path?: string | null
  created_at: string
}

export async function getTechnologies(accountId: string, token: string): Promise<AccountTechnology[]> {
  const res = await api.get(`/api/advisors/workspace/accounts/${accountId}/technologies`, { headers: headers(token) })
  return res.data
}
export async function createTechnology(accountId: string, data: Partial<AccountTechnology>, token: string): Promise<AccountTechnology> {
  const res = await api.post(`/api/advisors/workspace/accounts/${accountId}/technologies`, data, { headers: headers(token) })
  return res.data
}
export async function updateTechnology(accountId: string, techId: string, data: Partial<AccountTechnology>, token: string): Promise<AccountTechnology> {
  const res = await api.put(`/api/advisors/workspace/accounts/${accountId}/technologies/${techId}`, data, { headers: headers(token) })
  return res.data
}
export async function deleteTechnology(accountId: string, techId: string, token: string): Promise<void> {
  await api.delete(`/api/advisors/workspace/accounts/${accountId}/technologies/${techId}`, { headers: headers(token) })
}

export async function getInvoices(accountId: string, token: string): Promise<AccountInvoice[]> {
  const res = await api.get(`/api/advisors/workspace/accounts/${accountId}/invoices`, { headers: headers(token) })
  return res.data
}
// Signed URL for the retained original invoice file (404 for pre-retention rows)
export async function getInvoiceDownloadUrl(accountId: string, invoiceId: string, token: string): Promise<string> {
  const res = await api.get(`/api/advisors/workspace/accounts/${accountId}/invoices/${invoiceId}/download`, { headers: headers(token) })
  return res.data.url
}

export async function uploadInvoice(accountId: string, file: File, token: string): Promise<{ invoice: AccountInvoice; items: AccountTechnology[] }> {
  const form = new FormData()
  form.append('file', file)
  const res = await api.post(`/api/advisors/workspace/accounts/${accountId}/invoices`, form, {
    headers: { ...headers(token), 'Content-Type': 'multipart/form-data' },
    timeout: 90000
  })
  return res.data
}
export async function deleteInvoice(accountId: string, invoiceId: string, token: string): Promise<void> {
  await api.delete(`/api/advisors/workspace/accounts/${accountId}/invoices/${invoiceId}`, { headers: headers(token) })
}

// ── Account locations (with full addresses) ──
export interface AccountLocation {
  id: string
  account_id: string
  name: string | null
  type: string | null
  address_line1: string | null
  address_line2: string | null
  city: string
  state: string | null
  country: string
  zip: string | null
  employee_count: number | null
  is_headquarters: boolean
  phone: string | null
  notes: string | null
}

export async function getLocations(accountId: string, token: string): Promise<AccountLocation[]> {
  const res = await api.get(`/api/advisors/workspace/accounts/${accountId}/locations`, { headers: headers(token) })
  return res.data
}
export async function createLocation(accountId: string, data: Partial<AccountLocation>, token: string): Promise<AccountLocation> {
  const res = await api.post(`/api/advisors/workspace/accounts/${accountId}/locations`, data, { headers: headers(token) })
  return res.data
}
export async function updateLocation(accountId: string, locationId: string, data: Partial<AccountLocation>, token: string): Promise<AccountLocation> {
  const res = await api.put(`/api/advisors/workspace/accounts/${accountId}/locations/${locationId}`, data, { headers: headers(token) })
  return res.data
}
export async function deleteLocation(accountId: string, locationId: string, token: string): Promise<void> {
  await api.delete(`/api/advisors/workspace/accounts/${accountId}/locations/${locationId}`, { headers: headers(token) })
}

// MEETING BRIEFINGS (#188 round 2)
export interface BriefingSummary {
  id: string
  topic: string
  status: 'GENERATING' | 'READY' | 'FAILED'
  error: string | null
  generated_at: string | null
  created_at: string
  headline?: string | null
}

export interface BriefingStat { value: string; label: string }
export interface BriefingStakeholder { contact_id: string | null; name: string; title: string; note: string }
export interface BriefingNewsItem { date: string; title: string; fact: string; angle: string }
export interface BriefingLocation { id: string; name: string; hq: boolean; address: string }
export interface BriefingCaptureField { id: string; label: string; type: 'number' | 'select'; options: string[] | null }
export interface BriefingFlowStep { id: string; label: string; sub: string }
export interface BriefingEstateItem { id: string; label: string; tech_id: string | null }
export interface BriefingEstateCategory { id: string; name: string; badge: string | null; description: string; items: BriefingEstateItem[] }
export interface BriefingAngle { kind: string; title: string; body: string; opener: string }
export interface BriefingQuestion { id: string; text: string; rationale: string }
export interface BriefingPathStep { title: string; badge: string | null; body: string }
export interface BriefingSourceRow { data: string; source: string; how: string }

export interface BriefingContent {
  meta: { company: string; topic: string; headline: string; intro: string; assembled_note: string }
  snapshot: { stats: BriefingStat[]; narrative: string[]; focus_note: string; sources: string[] }
  room: {
    intro: string
    primary_contact: { contact_id: string | null; name: string; title: string; background: string; why_matters: string; how_to_engage: string; sources: string[] } | null
    stakeholders: BriefingStakeholder[]
  }
  news: { intro: string; items: BriefingNewsItem[]; verify_note: string | null; sources: string[] }
  footprint: { intro: string; locations: BriefingLocation[]; capture?: BriefingCaptureField[]; total_locations?: number; reported_location_count?: number | null }
  flow: { intro: string; steps: BriefingFlowStep[]; closing_note: string }
  estate: { intro: string; categories: BriefingEstateCategory[]; seams_note: string }
  angles: { intro: string; items: BriefingAngle[] }
  questions: { intro: string; items: BriefingQuestion[] }
  path: { intro: string; steps: BriefingPathStep[] }
  sources: { intro: string; rows: BriefingSourceRow[]; note: string | null }
}

export interface BriefingPresentConfig {
  accent?: string | null
  logo?: 'advisor' | 'default' | 'custom'
  logo_url?: string
  sections?: Record<string, boolean>
  text_scale?: 'normal' | 'large'
}

export interface BriefingState {
  stakeholder_roles: Record<string, string>
  locations: Record<string, { status: string; seats: number; fields?: Record<string, string | number> }>
  flow_confirm: Record<string, string>
  estate: Record<string, number>
  estate_notes?: Record<string, string>
  answers: Record<string, string>
  notes: Record<string, string>
  next_steps: Array<{ text: string; owner: string; due: string }>
  present_config?: BriefingPresentConfig
}

export interface Briefing extends BriefingSummary {
  account_id: string
  content: BriefingContent | null
  state: BriefingState | null
}

export async function listBriefings(accountId: string, token: string): Promise<BriefingSummary[]> {
  const res = await api.get(`/api/advisors/workspace/accounts/${accountId}/briefings`, { headers: headers(token) })
  return res.data
}
export async function createBriefing(accountId: string, topic: string, token: string): Promise<BriefingSummary> {
  const res = await api.post(`/api/advisors/workspace/accounts/${accountId}/briefings`, { topic }, { headers: headers(token) })
  return res.data
}
export async function getBriefing(briefingId: string, token: string): Promise<Briefing> {
  const res = await api.get(`/api/advisors/workspace/briefings/${briefingId}`, { headers: headers(token) })
  return res.data
}
export async function patchBriefingState(briefingId: string, patch: Partial<BriefingState>, token: string): Promise<void> {
  await api.patch(`/api/advisors/workspace/briefings/${briefingId}/state`, patch, { headers: headers(token) })
}
export async function deleteBriefing(briefingId: string, token: string): Promise<void> {
  await api.delete(`/api/advisors/workspace/briefings/${briefingId}`, { headers: headers(token) })
}
